<template>
  <nav>
    <v-row class="fill-height" align="center" v-if="!loadingData">
      <v-col cols="12" sm="8" md="6" offset-sm="2" offset-md="3" align="center">
        <AlertMessages
          v-if="alertValue"
          :alert-type="alertType"
          :alert-msg="alertMsg"
          @reset="alertValue = false"
        />
        <v-card max-width="800" elevation="0" rounded="xxl" color="background">
          <v-card-title
            class="text-h5 font-weight-bold text-center justify-center mb-4 no-break"
          >
            {{ $t("addpeopleWithoutAccountTitle") }}
          </v-card-title>
          <v-card-subtitle
            class="text-body-1 darktext--text d-none d-sm-block no-break"
          >
            {{ $t("addpeopleWithoutAccountText") }}
          </v-card-subtitle>

          <v-card rounded="xxl" class="mx-auto pa-5">
            <v-card-text>
              <v-form ref="form" @submit.prevent>
                <v-row
                  v-for="(participant, index) in participants"
                  :key="index"
                >
                  <div style="width: 100%" v-if="index != 0" class="my-2 px-5">
                    <v-divider light></v-divider>
                  </div>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="participant.first_name"
                      data-testid="participant-first-name-input"
                      solo
                      hide-details="auto"
                      class="rounded-xxl text-body-2"
                      :placeholder="$t('createOrderFirstNamePlaceholder')"
                      :rules="firstNameRules"
                      @keydown.enter.prevent
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="participant.last_name"
                      data-testid="participant-last-name-input"
                      solo
                      hide-details="auto"
                      class="rounded-xxl text-body-2"
                      :placeholder="$t('createOrderLastNamePlaceholder')"
                      :rules="lastNameRules"
                      @keydown.enter.prevent
                    />
                  </v-col>
                  <v-col cols="12">
                    <v-text-field
                      v-model="participant.email"
                      data-testid="participant-email-input"
                      solo
                      hide-details="auto"
                      class="rounded-xxl text-body-2"
                      :placeholder="$t('createOrderEmailPlaceholder')"
                      :rules="emailRulesWithLimit"
                      @keydown.enter.prevent
                    >
                      <template v-slot:append-outer>
                        <v-btn
                          v-if="participants.length > 1"
                          icon
                          small
                          color="error"
                          @click="removeParticipant(index)"
                          ><v-icon>mdi-delete-circle</v-icon></v-btn
                        >
                      </template>
                    </v-text-field>
                  </v-col>
                </v-row>
              </v-form>
            </v-card-text>
          </v-card>
          <div class="text-right mt-5 mr-9">
            <v-btn
              fab
              small
              color="accent"
              @click="addParticipant"
              data-testid="add-participant-button"
              ><v-icon>mdi-plus</v-icon></v-btn
            >
          </div>
        </v-card>
        <v-row>
          <v-col align="center">
            <FormFooter
              next-button-text="add"
              :loading="loading"
              :cancel-button-link="cancelLink"
              @proceedSubmit="shareWorkflow"
            />
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </nav>
</template>
<script>
import i18n from "../../plugins/i18n";
import { addWorkflowExternalPeople, getWorkflow } from "../../api/workflowApi";
import FormFooter from "../../components/FormFooter";
import AlertMessages from "../../components/AlertMessages";
import {
  ruleRequired,
  emailRuleValid,
  ruleValidMin2Max70Characters,
  ruleValidMinMax,
} from "../../rules/formRules.js";

export default {
  components: {
    FormFooter,
    AlertMessages,
  },
  data() {
    return {
      spaceId: window.$cookies.get("space_id"),
      selectedUsers: [],

      isLoading: false,
      alertMsg: "",
      alertValue: false,
      loading: false,
      showRedError: false,
      message: "",
      workflowId: this.$route.params.id,
      firstNameRules: ruleRequired("labelFirstName").concat(
        ruleValidMin2Max70Characters("labelFirstName")
      ),
      lastNameRules: ruleRequired("labelLastName").concat(
        ruleValidMin2Max70Characters("labelLastName")
      ),
      emailRules: ruleRequired("labelEmail").concat(emailRuleValid),
      emailRulesWithLimit: ruleRequired("labelEmail")
        .concat(emailRuleValid)
        .concat(ruleValidMinMax("labelEmail", 2, 255)),
      errors: [],
      loadingData: false,
      cancelLink: "/workflow/" + this.$route.params.id + "/people",
      previousRoute: null,
      roleInSpace: localStorage.space
        ? JSON.parse(localStorage.space).role
        : null,
      participants: [
        {
          first_name: "",
          last_name: "",
          email: "",
        },
      ],
    };
  },
  computed: {
    selectedUsersEmails() {
      return this.selectedUsers.map((entry) => {
        if (entry.type == "users") {
          if (entry.attributes.email) {
            return entry.attributes.email;
          }
        }
      });
    },
    workflow() {
      return this.$store.state.workflow;
    },
  },
  watch: {
    workflow: {
      immediate: true,
      async handler(to) {
        if (to) {
          this.loadingData = true;
          const response = await getWorkflow(this.workflowId);
          if (response.data.data.attributes.member_type == "participant") {
            this.$router.push({
              path: "/workflow/" + this.workflowId + "/people/add",
            });
          }
          if (response.data.data.attributes.is_broken) {
            this.$router.push({ name: "PageAccessDenied" });
          }
          this.loadingData = false;
        }
      },
    },
  },
  beforeRouteEnter(to, from, next) {
    if (from.path != null && from.path != "/redirect" && from.path != "/") {
      next((vm) => {
        (vm.cancelLink = from.path), (vm.previousRoute = from);
      });
    } else {
      next();
    }
  },
  methods: {
    addParticipant() {
      this.participants.push({
        first_name: "",
        last_name: "",
        email: "",
      });
    },
    removeParticipant(index) {
      this.participants.splice(index, 1);
    },

    async shareWorkflow() {
      this.valid = this.$refs.form.validate();
      const emails = this.participants.map((participant) =>
        participant.email.trim().toLowerCase()
      );
      const emailsUnique = [...new Set(emails)];
      if (emails.length !== emailsUnique.length && this.valid) {
        this.alertValue = true;
        this.alertType = "error";
        this.alertMsg = i18n.t("createOrderErrorDuplicateEmails");
      } else if (this.participants.length > 0 && this.valid) {
        this.loading = true;
        try {
          let data = {
            data: {
              type: "workflow-external-users",
              attributes: {
                participants: this.participants.map((participant) => ({
                  ...participant,
                  email: participant.email.trim().toLowerCase(),
                })),
              },
              relationships: {
                workflow: {
                  data: {
                    type: "workflows",
                    id: this.workflowId,
                  },
                },
              },
            },
          };
          const response = await addWorkflowExternalPeople(
            this.$route.params.id,
            data
          );
          if (response.status == 200) {
            if (this.previousRoute) {
              if (this.previousRoute.name !== null) {
                var params = this.previousRoute.params;
                params.peopleAddedInWorkflow = true;
                this.$router.push({
                  name: this.previousRoute.name,
                  params: params,
                });
              } else {
                this.$router.push({
                  name: "ExternalMembersInWorkflow",
                  params: {
                    id: this.$route.params.id,
                    peopleAddedInWorkflow: true,
                  },
                });
              }
            } else {
              this.$router.push({
                name: "PeopleInWorkflow",
                params: {
                  id: this.$route.params.id,
                  peopleAddedInWorkflow: true,
                },
              });
            }
          }
        } catch (e) {
          if (e.response) {
            this.alertValue = true;
            if (e.response.status == 403) {
              this.$router.push({ name: "PageAccessDenied" });
            } else if (e.response.status == 404) {
              this.$router.push({ name: "PageNotFound" });
            } else if (e.response.status == 422) {
              for (let i = 0; i < e.response.data.errors.length; i++) {
                this.alertValue = true;
                this.alertType = "error";
                this.alertMsg = e.response.data.errors[i].detail;
              }
            } else {
              this.alertType = "error";
              this.alertMsg = i18n.t("serverError");
            }
          }
        }
        this.loading = false;
      }
    },
  },
};
</script>
